import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import { range } from '@/utils';

import { DemoSliderControl } from '@/components/Demo';
import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';

import GridDemo from './GridDemo';
import Item from './Item';
import { GridStructure, BaseCodeWrapper } from './Grid.shared';

function ImplicitGridFixedHeight() {
  return (
    <GridDemo
      initialValues={{
        numOfChildren: 3,
        height: 300,
      }}
      customGrid={{
        gridTemplateColumns: '1fr',
      }}
      controls={[
        <SliderWrapper key="numOfChildren">
          <DemoSliderControl
            id="numOfChildren"
            label="Number of Children"
            min={1}
            max={5}
            step={1}
            handleFocusColor="white"
            wrapperStyle={{
              width: 400,
              maxWidth: '80vw',
            }}
          />
        </SliderWrapper>,
      ]}
    >
      {({ numOfChildren, height, isPerspectiveEnabled }) => {
        const cssCode = `\
.parent {
  display: grid;
  height: ${height}px;
}\
        `;
        return (
          <Row>
            <CodeWrapper>
              <ClientOnlySnippet
                omitCopyButton
                lockedToColorMode="dark"
                code={cssCode}
                lang="css"
              />
            </CodeWrapper>
            <Wrapper style={{ height }}>
              <Layer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                <GridStructure style={{ gap: 2 }}>
                  {range(numOfChildren).map((index) => (
                    <GridTrack key={index} />
                  ))}
                </GridStructure>
              </Layer>
              <Layer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(-10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                <ChildWrapper>
                  {range(numOfChildren).map((index) => (
                    <Item
                      key={index}
                      layout={false}
                      style={{
                        opacity: isPerspectiveEnabled ? 0.8 : 1,
                      }}
                    />
                  ))}
                </ChildWrapper>
              </Layer>
            </Wrapper>
          </Row>
        );
      }}
    </GridDemo>
  );
}

const SliderWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 64px;
  margin: 64px 0px;

  @media (max-width: 42.5rem) {
    flex-direction: column-reverse;
    align-items: center;
    margin: 48px 0px 32px 0px;
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 350px;
  height: 250px;
`;

const Layer = styled(motion.div)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transition: transform 500ms;
  will-change: transform;
`;

const ChildWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  gap: 2px;
  padding: 2px;
`;

const GridTrack = styled.div`
  border: 1px solid transparent;
  transform: translateY(1.5px);

  &:not(:last-of-type) {
    border-bottom: 2px dashed var(--color-gray-500);
  }
`;

const CodeWrapper = styled(BaseCodeWrapper)`
  min-width: 300px;
`;

export default ImplicitGridFixedHeight;
